import React, { useEffect, useState, useCallback, useRef } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { Link, useParams } from "react-router";
import { DebounceInput } from "react-debounce-input";
import { createColumnHelper } from "@tanstack/react-table";

import {
  setItemToStorage,
} from "../../../../system";
import {
  emptyString,
  pathConstant,
  loginConstants,
  managerProfileConstants,
  MENU_PLACEMENT,
  FILTER_LABEL,
} from "../../constants";
import {
  useSpinner,
  Option,
  Spinner,
  DataTable,
  SurveyStats,
  ValueContainer,
  UTCToDateFormat,
  loadingIndicator,
  paginatedPostRequestQueryBody,
} from "../../../../shared";
import { managerProfileActions } from "../../redux/actions";
import { individualStoreProfileActions } from "../../../shared-screens/redux/actions";
import ImportOrder from "../../../../shared/components/ImportOrder/ImportOrder";
import { brandProfileConstants } from "../../../shared-screens/constants";

const { MANAGERS_DATA, AND, OR, STATE, NAME } = managerProfileConstants;

//Manager profile
const ManagerProfile = (props) => {
  const {
    user,
    managerProfileReducer: {
      allLocations,
      managerDetails,
      collectiveData,
      managerDataPagination,
    },

    getManagerDetails,
    resetRestaurantProfile,
    getAllStoresByManagerId,
  } = props;

  const { managerId, groupId } = useParams();

  const filters = useRef({
    name: [],
    state: [],
  });

  const [searchString, setSearchString] = useState(emptyString);
  const [sorting, setSorting] = useState({ id: "name", desc: true });
  const [filteredDataValue, setFilteredDataValue] = useState(emptyString);

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Create query string for filters
  const generateQuery = (filterObj) => {
    //Create array of filters name from filters object e.g(['name','state','ownerName'])
    const filterNames = Object.keys(filterObj);
    const queryArray = filterNames.reduce((arr, name) => {
      const values = filterObj[name];
      //If multiple filter apply inside one filter then return with OR else only return one value e.g(['name="Monal Resto"']) or (['name="test 1" OR name="test 2"])
      if (values.length) {
        const filterValues = values.map((value) => `${name}="${value}"`);
        arr.push(`(${filterValues.join(OR)})`); // Surround filter values with parentheses
      }
      return arr;
    }, []);

    //If we select multiple filters e.g(state, name and ownerName) then each filter will be separated with AND e.g(['name="test 1" AND state="state 1" AND etc...])
    return queryArray.join(AND);
  };
  //Set filter for the select dropdown
  const setFilteredData = (property, value) => {
    //Save values against its filter name
    if (property === NAME) {
      filters.current.name = value;
    } else {
      filters.current.state = value;
    }
    const query = generateQuery(filters.current);
    setFilteredDataValue(query);
  };

  //Set unique states
  const uniqueStates = [...new Set(allLocations.map((loc) => loc.state))];

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const requestBody = paginatedPostRequestQueryBody(
      managerDataPagination,
      pageNumber,
      searchString,
      sortBy || sorting,
      filteredDataValue || emptyString,
      emptyString
    );
    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy);
    getAllStoresByManagerId(requestBody, managerId || groupId);
  };

  //Styling react select
  const customStyle = {
    //Changing default style of options in react select
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: state.isSelected ? "transparent" : "auto",
      color: "black",
    }),
  };

  //Handle store name click
  const handleClickStoreName = (profile) => {
    setItemToStorage(loginConstants.LOCATION_ID, profile.id);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify({
        name: pathConstant.MANAGER_PROFILE,
      })
    );
  };

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const profile = row.original;
      return profile?.name ? (
        <Link
          className={`table-links`}
          to={`/manager-profile/${managerId}/store-performance/${profile.id}`}
          state={{ name: pathConstant.MANAGER_PROFILE }}
          onClick={() => handleClickStoreName(profile)}
        >
          {profile.name}
        </Link>
      ) : (
        <span />
      );
    },
    // eslint-disable-next-line
    []
  );

  //Static headers for data table
  //"Cell" is being used to manipulate the data for displaying purpose
  const columnHelper = createColumnHelper();
  const franchiseProfile = [
    columnHelper.accessor("name", {
      header: () => "STORE NAME",
      cell: cellNameRenderer,
    }),
    columnHelper.accessor("createdAt", {
      header: () => "DATE ACTIVATED",
      cell: (row) => {
        const profile = row.row.original;
        return profile.createdAt
          ? UTCToDateFormat(profile.createdAt)
          : emptyString;
      },
    }),
    columnHelper.accessor("totalResponses", {
      header: () => "TOTAL RESPONSES",
      enableSorting: false,
    }),
  ];

  // Reset store profile
  useEffect(() => {
    resetRestaurantProfile();
    // eslint-disable-next-line
  }, []);

  // fetch data from these calls before the page loads
  useEffect(() => {
    getManagerDetails(groupId || managerId);
    getData(0);
    setItemToStorage(loginConstants.MANAGER_ID, managerId);
    setItemToStorage(loginConstants.BRAND_ID, user.brandId);

    // eslint-disable-next-line
  }, [searchString, filteredDataValue]);

  return (
    <div className="container-fluid py-3">
      <div className="notification-form card mb-3 shadow">
        <div className="card-body p-0">
          <div className="row">
            <div className="col-6">
              <h5 className="ps-4 py-3 mb-0">General</h5>
            </div>
            <div className="col-6 d-none">
              <ImportOrder />
            </div>
          </div>
          <hr className="p-0 m-0" />
          <div className="row px-4 py-3">
            <div className="col-12">
              <div className="row">
                <div className="col-12" data-testid="manager-name">
                  <span className="col-lg-2 col-md-3 col-6 d-inline-block survey_profile_labels bold-5">
                    Manager Name:
                  </span>
                  <span className="profile_lable_values">
                    {managerDetails?.data?.name}
                  </span>
                </div>
                <div className="col-12" data-testid="manager-id">
                  <span className="col-lg-2 col-md-3 col-6 d-inline-block survey_profile_labels bold-5">
                    Manager ID:
                  </span>
                  <span className="profile_lable_values">
                    {managerDetails?.data?.id}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="notification-form card shadow">
        <div className="card-body p-0">
          <div className="row">
            <div className="col-6 py-3">
              <h5 className="ps-4 mb-0">Stores</h5>
            </div>
            <div className="col-6 py-3">
              <div className="search-container ms-auto me-4">
                <DebounceInput
                  debounceTimeout={1000}
                  type="text"
                  className=" search-restaurants"
                  placeholder="Search"
                  id="search-store-locations"
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                />
                <i className="fa fa-search search-icon" aria-hidden="true"></i>
              </div>
            </div>
          </div>

          <hr className="mt-0" />

          <div className="row px-4 py-2">
            <div className="col-12 col-md-4">
              <Select
                name="name"
                options={[...allLocations]}
                components={{
                  IndicatorSeparator: () => null,
                  Option,
                  ValueContainer: ValueContainer,
                  LoadingIndicator: loadingIndicator,
                }}
                isMulti
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                onChange={(selectedOption) =>
                  setFilteredData(
                    NAME,
                    selectedOption.map((opt) => opt.name)
                  )
                }
                placeholder={FILTER_LABEL.ALL_STORES}
                isSearchable
                maxMenuHeight={200}
                menuPlacement={MENU_PLACEMENT.AUTO}
                styles={customStyle}
                isDisabled={spinner(MANAGERS_DATA)}
                isLoading={spinner(MANAGERS_DATA)}
              />
            </div>
            <div className="col-12 col-md-4">
              <Select
                name="state"
                options={[
                  ...uniqueStates
                    .filter((state) => state !== emptyString)
                    .map((state) => ({ id: state, state })),
                ]}
                components={{
                  IndicatorSeparator: () => null,
                  Option,
                  ValueContainer: ValueContainer,
                  LoadingIndicator: loadingIndicator,
                }}
                isMulti
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.state}
                onChange={(selectedOption) =>
                  setFilteredData(
                    STATE,
                    selectedOption.map((opt) => opt.state)
                  )
                }
                placeholder={FILTER_LABEL.ALL_STATES}
                isSearchable
                maxMenuHeight={200}
                menuPlacement={MENU_PLACEMENT.AUTO}
                styles={customStyle}
                isDisabled={spinner(MANAGERS_DATA)}
                isLoading={spinner(MANAGERS_DATA)}
              />
            </div>
          </div>

          <SurveyStats
            total={collectiveData.totalSurvey}
            positive={collectiveData.positiveResponses}
            negative={collectiveData.negativeResponses}
          />
          {spinner(MANAGERS_DATA) && <Spinner />}
          <div>
            <DataTable
              columns={franchiseProfile}
              data={allLocations}
              totalRows={managerDataPagination.totalElements}
              numberOfRowsPerPage={managerDataPagination.size}
              pageNumber={managerDataPagination.number + 1}
              onPageChange={(pageNum) => getData(pageNum)}
              onColumnSort={(sortBy) =>
                getData(managerDataPagination.number, sortBy)
              }
              sortedBy={sorting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.authentication.user,
  managerProfileReducer: state.managerProfileReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getManagerDetails: (managerId) =>
    dispatch(managerProfileActions.getManagerDetails(managerId)),
  getAllStoresByManagerId: (body, managerId) =>
    dispatch(managerProfileActions.getAllStoresByManagerId(body, managerId)),
  resetRestaurantProfile: () =>
    dispatch(individualStoreProfileActions.resetRestaurantProfile()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManagerProfile);
